export const WORKSPACE_DOMAIN_VERSION = 1;
export const WORKSPACE_AI_CONTEXT_VERSION = 1;
export const WORKSPACE_MAX_AI_INSTRUCTIONS_CHARS = 8000;
export const WORKSPACE_MAX_DOCUMENT_BYTES = 2 * 1024 * 1024;
export const WORKSPACE_MCP_SCOPES = Object.freeze([
  'workspace:search',
  'workspace:read',
  'workspace:create',
  'workspace:write',
]);

const ITEM_ID_RE = /^itm_[a-f0-9]{32}$/u;
const NOTEBOOK_ID_RE = /^[A-Za-z0-9._:-]{1,160}$/u;

export function createWorkspaceItemId() {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return `itm_${cryptoApi.randomUUID().replaceAll('-', '').toLowerCase()}`;
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `itm_${Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')}`;
  }
  throw new Error('workspace_secure_random_unavailable');
}

export function normalizeWorkspaceItemId(value) {
  const candidate = String(value || '').trim().toLowerCase();
  if (ITEM_ID_RE.test(candidate)) return candidate;
  // 19.36.89: Historic extension writers prefixed a 40-hex stable note ID
  // with a second `itm_`. The website previously erased these IDs during
  // metadata normalization, causing apparent collisions and lost mappings.
  // Keep only the 128-bit canonical portion; every client maps the same old
  // ID to the same new ID. Do NOT accept arbitrary malformed identifiers.
  const legacy = /^itm_(?:itm_?)?([a-f0-9]{32})(?:[a-f0-9]{8})?$/u.exec(candidate);
  return legacy ? `itm_${legacy[1]}` : '';
}

export function normalizeWorkspaceNotebookId(value) {
  const candidate = String(value || '').trim();
  return NOTEBOOK_ID_RE.test(candidate) ? candidate : '';
}

export function normalizeWorkspaceRelativePath(value) {
  const path = String(value || '').replaceAll('\\', '/').replace(/^\/+|\/+$/gu, '');
  if (!path || path.length > 1024 || path.includes('\0')) return '';
  const parts = path.split('/');
  if (parts.some((part) => !part || part === '.' || part === '..')) return '';
  return parts.join('/');
}

export function isWorkspacePublicPath(value) {
  const path = normalizeWorkspaceRelativePath(value);
  return Boolean(path) && path !== '.elasticnote' && !path.startsWith('.elasticnote/');
}


const WORKSPACE_EDITABLE_TEXT_EXTENSIONS = new Set([
  '.md', '.markdown', '.rst', '.adoc',
  '.bib', '.bst', '.cls', '.ltx', '.sty', '.tex', '.txt',
  '.c', '.cc', '.cpp', '.cxx', '.h', '.hh', '.hpp',
  '.py', '.pyi', '.r', '.jl', '.m', '.matlab', '.sh', '.bash', '.zsh', '.fish',
  '.js', '.jsx', '.mjs', '.cjs', '.ts', '.tsx', '.mts', '.cts',
  '.html', '.htm', '.css', '.scss', '.sass', '.less', '.vue', '.svelte',
  '.java', '.kt', '.kts', '.go', '.rs', '.swift', '.scala', '.lua', '.pl', '.pm', '.rb', '.php',
  '.sql', '.graphql', '.gql',
  '.csv', '.tsv', '.json', '.jsonl', '.ndjson', '.xml',
  '.yaml', '.yml', '.toml', '.ini', '.cfg', '.conf', '.env', '.properties',
]);

export function isWorkspaceEditableTextPath(value) {
  const path = normalizeWorkspaceRelativePath(value);
  if (!path || !isWorkspacePublicPath(path)) return false;
  const leaf = path.slice(path.lastIndexOf('/') + 1).toLowerCase();
  if (leaf === 'dockerfile' || leaf === 'makefile' || leaf === 'cmakelists.txt') return true;
  const dot = leaf.lastIndexOf('.');
  return dot > 0 && WORKSPACE_EDITABLE_TEXT_EXTENSIONS.has(leaf.slice(dot));
}

export function normalizeWorkspaceAiContext(value, now = Date.now()) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const content = String(value.content || '').trim().slice(0, WORKSPACE_MAX_AI_INSTRUCTIONS_CHARS);
  const deleted = value.deleted === true;
  if (!content && !deleted) return undefined;
  const updatedAtRaw = Number(value.updatedAt);
  return {
    version: WORKSPACE_AI_CONTEXT_VERSION,
    content: deleted ? '' : content,
    deleted,
    updatedAt: Number.isFinite(updatedAtRaw) && updatedAtRaw > 0 ? updatedAtRaw : now,
  };
}

export function mergeWorkspaceAiContext(localValue, portableValue) {
  const local = normalizeWorkspaceAiContext(localValue);
  const portable = normalizeWorkspaceAiContext(portableValue);
  if (!local) return portable;
  if (!portable) return local;
  return local.updatedAt >= portable.updatedAt ? local : portable;
}

export function effectiveWorkspaceAiInstructions(accountValue, notebookValue) {
  const account = String(accountValue || '').trim().slice(0, WORKSPACE_MAX_AI_INSTRUCTIONS_CHARS);
  const notebook = String(notebookValue || '').trim().slice(0, WORKSPACE_MAX_AI_INSTRUCTIONS_CHARS);
  const sections = [];
  if (account) sections.push(`Account instructions:\n${account}`);
  if (notebook) sections.push(`Notebook instructions:\n${notebook}`);
  return sections.join('\n\n');
}

export function buildWorkspaceDeepLink(origin, input) {
  const notebookId = normalizeWorkspaceNotebookId(input?.notebookId);
  if (!notebookId) throw new Error('invalid_notebook_id');
  const base = new URL('/workspace', origin);
  base.searchParams.set('notebook', notebookId);
  const itemId = normalizeWorkspaceItemId(input?.itemId);
  if (itemId) base.searchParams.set('item', itemId);
  if (Number.isFinite(Number(input?.line)) && Number(input.line) > 0) base.searchParams.set('line', String(Math.trunc(Number(input.line))));
  const heading = String(input?.heading || '').trim().slice(0, 240);
  if (heading) base.searchParams.set('heading', heading);
  const search = String(input?.search || '').trim().slice(0, 500);
  if (search) base.searchParams.set('search', search);
  return base.toString();
}

export function normalizeWorkspaceScopes(input) {
  const allowed = new Set(WORKSPACE_MCP_SCOPES);
  return [...new Set((Array.isArray(input) ? input : String(input || '').split(/\s+/u)).map((scope) => String(scope).trim()).filter((scope) => allowed.has(scope)))];
}

export function hasWorkspaceScope(scopes, required) {
  const current = new Set(normalizeWorkspaceScopes(scopes));
  return current.has(required);
}
